import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Trophy, Calendar, GraduationCap, Star, HeartPulse, Briefcase, Users, Search, Globe, LayoutGrid } from "lucide-react";
import { T } from "../context/LanguageContext";
import { LiveTicker } from "../components/LiveTicker";
import { TrendingStrip } from "../components/TrendingStrip";
import { ArticleStrip } from "../components/ArticleStrip";
import { AIContent } from "../components/AIContent";
import { ListenButton } from "../components/ListenButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const exploreCards = [
  { to: "/sports", icon: MapPin, title: "Sports & Games", desc: "From global to grassroots", img: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&w=600&q=60" },
  { to: "/teams", icon: Trophy, title: "Teams & Leagues", desc: "Follow your favourites", img: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=600&q=60" },
  { to: "/events", icon: Calendar, title: "Events & Scores", desc: "Live, upcoming and results", img: "https://images.unsplash.com/photo-1459865264687-595d652de67e?auto=format&fit=crop&w=600&q=60" },
  { to: "/schools", icon: GraduationCap, title: "Schools & Colleges", desc: "Building brighter futures", img: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=600&q=60" },
  { to: "/halloffame", icon: Star, title: "Halls of Fame", desc: "Legends inspire", img: "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?auto=format&fit=crop&w=600&q=60" },
  { to: "/health", icon: HeartPulse, title: "Sports Health", desc: "Move. Live. Be well.", img: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=600&q=60" },
  { to: "/news", icon: Briefcase, title: "Sports News", desc: "People. Innovation. Impact.", img: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=600&q=60" },
  { to: "/community", icon: Users, title: "Community", desc: "Stronger together", img: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=600&q=60" },
];

const continents = [
  { name: "North America", left: "14%", top: "30%", color: "bg-[#0055FF]" },
  { name: "South America", left: "26%", top: "68%", color: "bg-amber-500" },
  { name: "Europe", left: "47%", top: "24%", color: "bg-red-500" },
  { name: "Africa", left: "49%", top: "56%", color: "bg-amber-400" },
  { name: "Asia", left: "68%", top: "34%", color: "bg-emerald-500" },
  { name: "Oceania", left: "82%", top: "72%", color: "bg-purple-500" },
];

export default function Home() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [spotlight, setSpotlight] = useState(null);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/spotlight`).then((r) => setSpotlight(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (query.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(() => {
      axios.get(`${API}/search`, { params: { q: query } }).then((r) => setResults(r.data)).catch(() => {});
    }, 300);
  }, [query]);

  return (
    <div id="page-content">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-[#EBF2FF] to-[#DCEAFF]">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-8 px-4 pb-10 pt-12 sm:px-6 lg:grid-cols-2 lg:items-center lg:pt-16">
          <div className="fade-up">
            <h1 className="text-4xl font-extrabold leading-[1.08] tracking-tight text-[#0A2540] sm:text-5xl lg:text-6xl">
              <T>Every Sport.</T> <T>Every Place.</T><br /><T>Everyone.</T>
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">
              <T>Discover teams, leagues, athletes, events and sports communities — from your village to the world.</T>
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/countries" data-testid="explore-location-button" className="inline-flex items-center gap-2 rounded-xl bg-[#0055FF] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0040CC]">
                <MapPin className="h-4 w-4" /> <T>Explore by Location</T>
              </Link>
              <Link to="/sports" data-testid="browse-sports-button" className="inline-flex items-center gap-2 rounded-xl border-2 border-[#0055FF] bg-white px-5 py-3 text-sm font-bold text-[#0055FF] transition-colors hover:bg-blue-50">
                <LayoutGrid className="h-4 w-4" /> <T>Browse All Sports</T>
              </Link>
              <ListenButton targetId="page-content" />
            </div>
            <p className="mt-8 font-script text-2xl text-[#0055FF] sm:text-3xl" style={{ transform: "rotate(-2deg)" }}>
              A More Active Brighter World
            </p>
          </div>
          <div className="relative hidden lg:block">
            <div className="grid grid-cols-3 gap-3">
              <img src="https://images.unsplash.com/photo-1517927033932-b3d18e61fb3a?auto=format&fit=crop&w=400&q=70" alt="Footballer" className="mt-8 h-64 w-full rounded-2xl object-cover shadow-lg" />
              <img src="https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=400&q=70" alt="Basketball player" className="h-72 w-full rounded-2xl object-cover shadow-lg" />
              <img src="https://images.unsplash.com/photo-1552674605-db6ffd4facb5?auto=format&fit=crop&w=400&q=70" alt="Runner" className="mt-12 h-60 w-full rounded-2xl object-cover shadow-lg" />
            </div>
            <p className="absolute -right-2 top-0 font-script text-2xl text-[#0055FF]" style={{ transform: "rotate(3deg)" }}>
              Different People Same Passion
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mx-auto max-w-3xl px-4 pb-12 sm:px-6">
          <div className="relative">
            <div className="flex items-center rounded-full border border-slate-200 bg-white py-1.5 pl-5 pr-1.5 shadow-lg">
              <Search className="h-5 w-5 shrink-0 text-slate-400" />
              <input
                data-testid="global-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search sports, teams, leagues, athletes, schools or places"
                className="w-full bg-transparent px-3 py-2 text-sm text-slate-800 outline-none placeholder:text-slate-400"
              />
              <button data-testid="global-search-button" className="rounded-full bg-[#0055FF] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#0040CC]">
                <T>Search</T>
              </button>
            </div>
            {results.length > 0 && (
              <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl" data-testid="search-results">
                {results.map((r, i) => (
                  <button key={i} data-testid={`search-result-${i}`} onClick={() => { setQuery(""); navigate(r.link); }} className="flex w-full items-center justify-between px-5 py-3 text-left hover:bg-blue-50">
                    <span className="text-sm font-semibold text-slate-800">{r.name}<span className="ml-2 text-xs font-normal text-slate-400">{r.detail}</span></span>
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase text-[#0055FF]">{r.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Trending strip */}
      <TrendingStrip />

      {/* Explore cards */}
      <section className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6">
        <div className="flex items-end justify-between">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"><T>Explore SportsPulse</T></h2>
          <p className="hidden text-sm text-slate-500 sm:block"><T>More to explore. A bigger, brighter sporting world.</T></p>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="explore-cards">
          {exploreCards.map((c) => (
            <Link key={c.to + c.title} to={c.to} data-testid={`explore-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
              <div className="h-28 overflow-hidden">
                <img src={c.img} alt={c.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </div>
              <div className="flex items-center gap-3 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0055FF] text-white">
                  <c.icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-bold text-slate-900"><T>{c.title}</T></p>
                  <p className="text-xs text-slate-500"><T>{c.desc}</T></p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Live + Map */}
      <section className="mx-auto grid max-w-[1400px] grid-cols-1 gap-8 px-4 pb-14 sm:px-6 lg:grid-cols-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-3">
          <div className="flex items-center gap-2">
            <span className="live-pulse h-3 w-3 rounded-full bg-red-500" />
            <h2 className="text-xl font-bold text-slate-900 sm:text-2xl"><T>Live Around the World</T></h2>
          </div>
          <p className="mb-5 mt-1 text-sm text-slate-500"><T>Real-time scores and events from every corner of the globe.</T></p>
          <LiveTicker />
        </div>
        <div className="rounded-2xl border border-purple-200 bg-purple-50 p-6 lg:col-span-2">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-purple-600" />
            <h2 className="text-xl font-bold text-slate-900 sm:text-2xl"><T>Discover by Location</T></h2>
          </div>
          <p className="mt-1 text-sm text-slate-600"><T>Explore sports, teams, events and communities around the world.</T></p>
          <div className="relative mt-6 h-64 overflow-hidden rounded-xl bg-purple-100" data-testid="world-map">
            <img src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?auto=format&fit=crop&w=1200&q=60" alt="World map" className="absolute inset-0 h-full w-full object-cover opacity-30" />
            {continents.map((c) => (
              <Link to="/countries" key={c.name} data-testid={`map-pin-${c.name.toLowerCase().replace(/\s/g, "-")}`} className="group absolute -translate-x-1/2 -translate-y-1/2 text-center" style={{ left: c.left, top: c.top }}>
                <span className={`pin-bounce mx-auto block h-4 w-4 rounded-full ${c.color} ring-4 ring-white`} />
                <span className="mt-1 block text-[11px] font-bold text-slate-800">{c.name}</span>
                <span className="block text-[10px] text-purple-700 opacity-0 transition-opacity group-hover:opacity-100"><T>Explore</T></span>
              </Link>
            ))}
          </div>
          <p className="mt-4 text-right font-script text-xl text-purple-600">Same Game, A Brighter Tomorrow</p>
        </div>
      </section>

      {/* Athlete Spotlight */}
      {spotlight && (
        <section className="mx-auto max-w-[1400px] px-4 pb-14 sm:px-6" data-testid="athlete-spotlight">
          <div className="overflow-hidden rounded-2xl border border-orange-200 bg-orange-50">
            <div className="grid grid-cols-1 gap-6 p-6 sm:p-8 lg:grid-cols-3">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white">
                  <Star className="h-3.5 w-3.5" /> <T>Today's Athlete Spotlight</T>
                </span>
                <h2 className="mt-4 text-2xl font-extrabold text-slate-900 sm:text-3xl">{spotlight.name}</h2>
                <p className="mt-1 text-sm font-semibold text-orange-700">{spotlight.sport} · {spotlight.country}</p>
                <p className="mt-4 text-sm italic leading-relaxed text-slate-700">“{spotlight.citation}”</p>
                <p className="mt-4 text-xs text-slate-500"><T>Hall</T>: {spotlight.hall} · <T>Inducted</T>: {spotlight.inducted}</p>
                <Link to="/halloffame" data-testid="spotlight-hof-link" className="mt-5 inline-block rounded-full bg-[#0055FF] px-4 py-2 text-xs font-bold text-white hover:bg-[#0040CC]"><T>Explore Halls of Fame</T> →</Link>
              </div>
              <div className="lg:col-span-2">
                <AIContent topic={`Athlete spotlight: ${spotlight.name}`} context={`Write an inspiring daily spotlight on ${spotlight.name} (${spotlight.sport}, ${spotlight.country}), inducted into ${spotlight.hall} in ${spotlight.inducted}. Official citation: "${spotlight.citation}". Cover their journey, legacy and what young athletes can learn. Do not invent specific statistics beyond the citation.`} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Editorial articles */}
      <section className="mx-auto max-w-[1400px] px-4 pb-14 sm:px-6">
        <ArticleStrip title="From the Editorial Desk" limit={3} />
      </section>

      {/* AI content */}
      <section className="mx-auto max-w-[1400px] px-4 pb-16 sm:px-6">
        <AIContent topic="Welcome to SportsPulse.pro — the global sports portal" context="Explain how SportsPulse organizes world sport by place, sport, institution, competition, people and history — from village pitches to world championships. Inspire visitors to explore, play, volunteer and learn." />
      </section>
    </div>
  );
}
